import React from 'react';
import { motion } from 'framer-motion';
import { RiskScore } from '../../types';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from 'recharts';
import { ShieldCheck, TrendingUp, AlertCircle, Info } from 'lucide-react';
interface AIRiskScoringProps {
  riskData: RiskScore;
}
export function AIRiskScoring({
  riskData
}: AIRiskScoringProps) {
  const getScoreColor = (score: number) => {
    if (score <= 30) return 'text-green-600';
    if (score <= 60) return 'text-yellow-600';
    return 'text-red-600';
  };
  const getLevelColor = (level: string) => {
    if (level === 'Low') return 'bg-green-100 text-green-800';
    if (level === 'Medium') return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-2632">
      <div className="flex justify-between items-start mb-6" data-id="element-2633">
        <div data-id="element-2634">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center" data-id="element-2635">
            <ShieldCheck className="h-5 w-5 text-blue-600 mr-2" data-id="element-2636" />
            AI Risk Score
          </h3>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2637">
            Powered by Renta AI Analysis
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(riskData.level)}`} data-id="element-2638">
          {riskData.level} Risk
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-2639">
        {/* Score Display */}
        <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl border border-gray-100" data-id="element-2640">
          <div className="relative" data-id="element-2641">
            <svg className="w-40 h-40 transform -rotate-90" data-id="element-2642">
              <circle className="text-gray-200" strokeWidth="12" stroke="currentColor" fill="transparent" r="70" cx="80" cy="80" data-id="element-2643" />
              <motion.circle className={getScoreColor(riskData.score)} strokeWidth="12" strokeDasharray={440} strokeDashoffset={440 - 440 * (100 - riskData.score) / 100} // Invert for display: High score = High risk (bad)
            strokeLinecap="round" stroke="currentColor" fill="transparent" r="70" cx="80" cy="80" initial={{
              strokeDashoffset: 440
            }} animate={{
              strokeDashoffset: 440 - 440 * (100 - riskData.score) / 100
            }} transition={{
              duration: 1.5,
              ease: 'easeOut'
            }} data-id="element-2644" />
            </svg>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center" data-id="element-2645">
              <span className={`text-4xl font-bold ${getScoreColor(riskData.score)}`} data-id="element-2646">
                {riskData.score}
              </span>
              <span className="block text-xs text-gray-500" data-id="element-2647">/ 100</span>
            </div>
          </div>
          <p className="mt-4 text-sm font-medium text-gray-700" data-id="element-2648">
            {riskData.confidence}% Confidence Score
          </p>
        </div>

        {/* Factors Breakdown */}
        <div className="space-y-4" data-id="element-2649">
          <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2650">
            Contributing Factors
          </h4>

          <div data-id="element-2651">
            <div className="flex justify-between text-xs mb-1" data-id="element-2652">
              <span className="text-gray-600" data-id="element-2653">Income Stability</span>
              <span className="font-medium text-gray-900" data-id="element-2654">
                {riskData.factors.incomeStability}/100
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2655">
              <motion.div className="bg-blue-600 h-2 rounded-full" initial={{
              width: 0
            }} animate={{
              width: `${riskData.factors.incomeStability}%`
            }} transition={{
              duration: 1,
              delay: 0.2
            }} data-id="element-2656" />
            </div>
          </div>

          <div data-id="element-2657">
            <div className="flex justify-between text-xs mb-1" data-id="element-2658">
              <span className="text-gray-600" data-id="element-2659">Payment History</span>
              <span className="font-medium text-gray-900" data-id="element-2660">
                {riskData.factors.paymentHistory}/100
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2661">
              <motion.div className="bg-green-600 h-2 rounded-full" initial={{
              width: 0
            }} animate={{
              width: `${riskData.factors.paymentHistory}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} data-id="element-2662" />
            </div>
          </div>

          <div data-id="element-2663">
            <div className="flex justify-between text-xs mb-1" data-id="element-2664">
              <span className="text-gray-600" data-id="element-2665">Credit Score Impact</span>
              <span className="font-medium text-gray-900" data-id="element-2666">High</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2667">
              <motion.div className="bg-purple-600 h-2 rounded-full" initial={{
              width: 0
            }} animate={{
              width: '85%'
            }} transition={{
              duration: 1,
              delay: 0.4
            }} data-id="element-2668" />
            </div>
          </div>

          <div className="pt-2" data-id="element-2669">
            <div className="flex items-start p-3 bg-blue-50 rounded-lg" data-id="element-2670">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-2671" />
              <p className="text-xs text-blue-800" data-id="element-2672">
                Employment Type:{' '}
                <span className="font-semibold" data-id="element-2673">
                  {riskData.factors.employmentType}
                </span>
                . Permanent employment positively impacts your score.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Trend Chart */}
      <div className="border-t border-gray-100 pt-6" data-id="element-2674">
        <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center" data-id="element-2675">
          <TrendingUp className="h-4 w-4 mr-2 text-gray-500" data-id="element-2676" />
          6-Month Risk Trend
        </h4>
        <div className="h-48 w-full" data-id="element-2677">
          <ResponsiveContainer width="100%" height="100%" data-id="element-2678">
            <LineChart data={riskData.trend} data-id="element-2679">
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
              fontSize: 12,
              fill: '#6B7280'
            }} data-id="element-2680" />
              <YAxis hide domain={[0, 100]} data-id="element-2681" />
              <Tooltip contentStyle={{
              borderRadius: '8px',
              border: 'none',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }} data-id="element-2682" />
              <Line type="monotone" dataKey="score" stroke="#1D4ED8" strokeWidth={3} dot={{
              r: 4,
              fill: '#1D4ED8',
              strokeWidth: 2,
              stroke: '#fff'
            }} data-id="element-2683" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recommendations */}
      <div className="mt-6 bg-yellow-50 rounded-lg p-4 border border-yellow-100" data-id="element-2684">
        <h4 className="text-sm font-medium text-yellow-900 mb-2 flex items-center" data-id="element-2685">
          <AlertCircle className="h-4 w-4 mr-2" data-id="element-2686" />
          Recommendations to Improve
        </h4>
        <ul className="list-disc list-inside text-xs text-yellow-800 space-y-1 ml-1" data-id="element-2687">
          {riskData.recommendations.map((rec, idx) => <li key={idx} data-id="element-2688">{rec}</li>)}
        </ul>
      </div>
    </div>;
}